import {
  CustomCanvasContext,
  CustomCanvasGradient
} from "./CustomCanvasContext";

// direction:"ltr"
// filter:"none"
// fontKerning:"auto"
// fontStretch:"normal"
// fontVariantCaps:"normal"
// globalCompositeOperation:"source-over"
// imageSmoothingEnabled:true
// imageSmoothingQuality:"low"
// letterSpacing:"0px"
// lineCap:"butt"
// lineWidth:1
// shadowBlur:0
// shadowColor:"rgba(0, 0, 0, 0)"
// shadowOffsetX:0
// shadowOffsetY:0
// textRendering:"auto"
// wordSpacing:"0px"

// clip
// createConicGradient
// createImageData
// direction
// drawFocusIfNeeded
// filter
// fontKerning
// fontStretch
// fontVariantCaps
// getContextAttributes
// getLineDash
// getTransform
// globalCompositeOperation
// imageSmoothingEnabled
// imageSmoothingQuality
// isContextLost
// letterSpacing
// reset
// resetTransform
// roundRect
// setTransform
// shadowBlur
// shadowColor
// shadowOffsetX
// shadowOffsetY
// textRendering
// wordSpacing

export class YZCanvasContext {
  canvas: HTMLCanvasElement;

  context: CanvasRenderingContext2D;
  // context: CanvasRenderingContext2D
  // | WebGLRenderingContext
  // | WebGL2RenderingContext
  // | GPUCanvasContext
  // | ImageBitmapRenderingContext
  customContext?: CustomCanvasContext;

  // https://developer.mozilla.org/en-US/docs/Web/API/Path2D
  currentPath2D: Path2D = new Path2D();
  path2Ds: Path2D[] = [];

  // https://developer.mozilla.org/en-US/docs/Web/API/DOMMatrix
  currentMatrix: DOMMatrix = new DOMMatrix();
  // currentMatrix = document
  //   .createElementNS("http://www.w3.org/2000/svg", "svg")
  //   .createSVGMatrix();

  hasSetCustomStrokeStyle = false;
  hasSetCustomFillStyle = false;

  constructor(
    canvas: HTMLCanvasElement,
    width?: number,
    height?: number,
    enableCustomContext = false // 性能影响
  ) {
    this.canvas = canvas;
    this.context = canvas.getContext("2d", {
      willReadFrequently: true
    }) as CanvasRenderingContext2D;
    if (enableCustomContext) {
      this.customContext = new CustomCanvasContext({
        canvas: this.canvas,
        width: width || this.canvas.width,
        height: height || this.canvas.height
      });
    }
  }

  set lineWidth(lineWidth: number) {
    this.hasSetCustomStrokeStyle = true;
    this.context.lineWidth = lineWidth;
    if (this.customContext) this.customContext.lineWidth = lineWidth;
  }

  get lineWidth(): number {
    return this.context.lineWidth;
  }

  set lineCap(lineCap: CanvasLineCap) {
    this.hasSetCustomStrokeStyle = true;
    this.context.lineCap = lineCap;
    if (this.customContext) this.customContext.lineCap = lineCap;
  }

  get lineCap(): CanvasLineCap {
    return this.context.lineCap;
  }

  set lineJoin(lineJoin: CanvasLineJoin) {
    this.hasSetCustomStrokeStyle = true;
    this.context.lineJoin = lineJoin;
    if (this.customContext) this.customContext.lineCap = lineJoin;
  }

  get lineJoin(): CanvasLineJoin {
    return this.context.lineJoin;
  }

  set lineDashOffset(lineDashOffset: number) {
    this.hasSetCustomStrokeStyle = true;
    this.context.lineDashOffset = lineDashOffset;
    if (this.customContext) this.customContext.lineDashOffset = lineDashOffset;
  }

  get lineDashOffset(): number {
    return this.context.lineDashOffset;
  }

  set miterLimit(miterLimit: number) {
    this.hasSetCustomStrokeStyle = true;
    this.context.miterLimit = miterLimit;
    if (this.customContext) this.customContext.miterLimit = miterLimit;
  }

  get miterLimit(): number {
    return this.context.miterLimit;
  }

  set globalAlpha(alpha: number) {
    this.context.globalAlpha = alpha;
    if (this.customContext) this.customContext.globalAlpha = alpha;
  }

  get globalAlpha(): number {
    return this.context.globalAlpha;
  }

  // rgb(255,221,0)
  // rgba(255,221,0,1)
  // #000000
  set fillStyle(
    style:
      | string
      | CanvasGradient
      | CanvasPattern
      | { gradient: CanvasGradient; gradientCustom: CustomCanvasGradient }
  ) {
    this.hasSetCustomFillStyle = true;
    if (
      typeof style == "string" ||
      style instanceof CanvasGradient ||
      style instanceof CanvasPattern
    ) {
      this.context.fillStyle = style;
      if (this.customContext) this.customContext.fillStyle = style;
    } else {
      this.context.fillStyle = style.gradient;
      if (this.customContext)
        this.customContext.fillStyle = style.gradientCustom;
    }
  }

  get fillStyle(): string | CanvasGradient | CanvasPattern {
    return this.context.fillStyle;
  }

  set strokeStyle(
    style:
      | string
      | CanvasGradient
      | CanvasPattern
      | { gradient: CanvasGradient; gradientCustom: CustomCanvasGradient }
  ) {
    this.hasSetCustomStrokeStyle = true;
    if (
      typeof style == "string" ||
      style instanceof CanvasGradient ||
      style instanceof CanvasPattern
    ) {
      this.context.strokeStyle = style;
      if (this.customContext) this.customContext.strokeStyle = style;
    } else {
      this.context.strokeStyle = style.gradient;
      if (this.customContext)
        this.customContext.strokeStyle = style.gradientCustom;
    }
  }

  get strokeStyle(): string | CanvasGradient | CanvasPattern {
    return this.context.strokeStyle;
  }

  // font:"10px sans-serif"
  set font(font: string) {
    this.context.font = font;
    if (this.customContext) this.customContext.font = font;
  }

  get font(): string {
    return this.context.font;
  }

  set textBaseline(textBaseline: CanvasTextBaseline) {
    this.context.textBaseline = textBaseline;
    if (this.customContext) this.customContext.textBaseline = textBaseline;
  }

  get textBaseline(): CanvasTextBaseline {
    return this.context.textBaseline;
  }

  set textAlign(textAlign: CanvasTextAlign) {
    this.context.textAlign = textAlign;
    if (this.customContext) this.customContext.textAlign = textAlign;
  }

  get textAlign(): CanvasTextAlign {
    return this.context.textAlign;
  }

  fill(path?: Path2D) {
    if (path) {
      this.context.fill(path);
      /// customContext Not yet implemented
    } else {
      this.context.fill();
      if (this.customContext) this.customContext.fill();
    }
    return this;
  }

  fillRect(x: number, y: number, w: number, h: number) {
    this.context.fillRect(x, y, w, h);
    if (this.customContext) this.customContext.fillRect(x, y, w, h);
    return this;
  }

  fillText(text: string, x: number, y: number, maxWidth?: number | undefined) {
    this.hasSetCustomFillStyle = false;
    this.context.fillText(text, x, y, maxWidth);
    if (this.customContext) this.customContext.fillText(text, x, y);
    return this;
  }

  setLineDash(dashArray: number[]) {
    this.context.setLineDash(dashArray);
    if (this.customContext) this.customContext.setLineDash(dashArray);
    return this;
  }

  stroke(path?: Path2D) {
    if (path) {
      this.context.stroke(path);
      /// customContext Not yet implemented
    } else {
      this.context.stroke();
      if (this.customContext) this.customContext.stroke();
    }
    return this;
  }

  strokeText(
    text: string,
    x: number,
    y: number,
    maxWidth?: number | undefined
  ) {
    this.context.strokeText(text, x, y, maxWidth);
    if (this.customContext) this.customContext.strokeText(text, x, y);
    return this;
  }

  strokeRect(x: number, y: number, w: number, h: number) {
    this.context.strokeRect(x, y, w, h);
    if (this.customContext) this.customContext.strokeRect(x, y, w, h);
    return this;
  }

  measureText(text: string): TextMetrics {
    return this.context.measureText(text);
    /// customContext Not yet implemented
  }

  clearRect(x = 0, y = 0, w = this.canvas.width, h = this.canvas.height) {
    this.context.clearRect(x, y, w, h);
    if (this.customContext) this.customContext.clearRect(x, y, w, h);
    return this;
  }

  save() {
    this.context.save();
    if (this.customContext) this.customContext.save();
    this.currentMatrix = new DOMMatrix();
    this.path2Ds.push(this.currentPath2D);
    this.currentPath2D = new Path2D();
    return this;
  }

  restore() {
    this.context.restore();
    if (this.customContext) this.customContext.restore();
    this.hasSetCustomStrokeStyle = false;
    this.hasSetCustomFillStyle = false;
    this.currentMatrix = new DOMMatrix();
    this.currentPath2D = this.path2Ds.pop() || new Path2D();
    return this;
  }

  beginPath() {
    this.context.beginPath();
    if (this.customContext) this.customContext.beginPath();
    return this;
  }

  closePath() {
    this.context.closePath();
    if (this.customContext) this.customContext.closePath();
    this.currentPath2D.closePath();
    return this;
  }

  changePath2D(path = this.currentPath2D, transform = this.currentMatrix) {
    const newPath = new Path2D();
    newPath.addPath(path, transform);
    this.currentPath2D = newPath;
  }

  isPointInStroke(path: Path2D, x: number, y: number): boolean;
  isPointInStroke(x: number, y: number): boolean;
  isPointInStroke(...args: any[]): boolean {
    if (args[0] instanceof Path2D) {
      return this.context.isPointInStroke(args[0], args[1], args[2]);
    }
    return this.context.isPointInStroke(args[0], args[1]);
    /// customContext Not yet implemented
  }

  isPointInPath(path: Path2D, x: number, y: number): boolean;
  isPointInPath(x: number, y: number): boolean;
  isPointInPath(...args: any[]): boolean {
    if (args[0] instanceof Path2D) {
      return this.context.isPointInPath(args[0], args[1], args[2]);
    }
    return this.context.isPointInPath(args[0], args[1]);
    /// customContext Not yet implemented
  }

  // transform(a: number, b: number, c: number, d: number, e: number, f: number) {
  //   this.context.transform(a, b, c, d, e, f);
  //   if (this.customContext) this.customContext.transform(a, b, c, d, e, f);
  //   return this;
  // }

  translate(x: number, y: number) {
    this.context.translate(x, y);
    if (this.customContext) {
      this.customContext.translate(x, y);
    }
    this.currentMatrix.translate(x, y);
    this.changePath2D();
    return this;
  }

  rotate(radian: number, centerX = 0, centerY = 0) {
    /// 移动的是坐标系
    this.translate(centerX, centerY);
    this.context.rotate(radian);
    if (this.customContext) {
      this.customContext.rotate(radian);
    }
    this.currentMatrix.rotate(radian);
    this.translate(-centerX, -centerY);
    this.changePath2D();
    return this;
  }

  scale(x: number, y = x, centerX = 0, centerY = 0) {
    /// 移动的是坐标系
    this.translate(centerX, centerY);
    this.context.scale(x, y);
    if (this.customContext) {
      this.customContext.scale(x, y);
    }
    this.currentMatrix.scale(x, y);
    this.translate(-centerX, -centerY);
    this.changePath2D();
    return this;
  }

  moveTo(x: number, y: number) {
    this.context.moveTo(x, y);
    if (this.customContext) this.customContext.moveTo(x, y);
    this.currentPath2D.moveTo(x, y);
    return this;
  }

  lineTo(x: number, y: number) {
    this.context.lineTo(x, y);
    if (this.customContext) this.customContext.lineTo(x, y);
    this.currentPath2D.lineTo(x, y);
    return this;
  }

  arc(
    x: number,
    y: number,
    radius: number,
    startAngle: number,
    endAngle: number,
    counterClockwise?: boolean
  ) {
    this.context.arc(x, y, radius, startAngle, endAngle, counterClockwise);
    if (this.customContext)
      this.customContext.arc(
        x,
        y,
        radius,
        startAngle,
        endAngle,
        counterClockwise ? -1 : 1
      );
    this.currentPath2D.arc(
      x,
      y,
      radius,
      startAngle,
      endAngle,
      counterClockwise
    );
    return this;
  }

  arcTo(x1: number, y1: number, x2: number, y2: number, radius: number) {
    this.context.arcTo(x1, y1, x2, y2, radius);
    if (this.customContext) this.customContext.arcTo(x1, y1, x2, y2, radius);
    this.currentPath2D.arcTo(x1, y1, x2, y2, radius);
    return this;
  }

  ellipse(
    x: number,
    y: number,
    radiusX: number,
    radiusY: number,
    rotation: number,
    startAngle: number,
    endAngle: number,
    counterClockwise?: boolean
  ) {
    this.context.ellipse(
      x,
      y,
      radiusX,
      radiusY,
      rotation,
      startAngle,
      endAngle,
      counterClockwise
    );
    /// customContext Not yet implemented
    this.currentPath2D.ellipse(
      x,
      y,
      radiusX,
      radiusY,
      rotation,
      startAngle,
      endAngle,
      counterClockwise
    );
    return this;
  }

  quadraticCurveTo(cpx: number, cpy: number, x: number, y: number) {
    this.context.quadraticCurveTo(cpx, cpy, x, y);
    if (this.customContext) this.customContext.quadraticCurveTo(cpx, cpy, x, y);
    this.currentPath2D.quadraticCurveTo(cpx, cpy, x, y);
    return this;
  }

  bezierCurveTo(
    cp1x: number,
    cp1y: number,
    cp2x: number,
    cp2y: number,
    x: number,
    y: number
  ) {
    this.context.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y);
    if (this.customContext)
      this.customContext.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y);
    this.currentPath2D.bezierCurveTo(cp1x, cp1y, cp2x, cp2y, x, y);
    return this;
  }

  rect(x: number, y: number, w: number, h: number) {
    this.context.rect(x, y, w, h);
    if (this.customContext) this.customContext.rect(x, y, w, h);
    this.currentPath2D.rect(x, y, w, h);
    return this;
  }

  // { 0: "#ffffff", 1: "#ff0000" }
  createLinearGradient(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    colorStops?: Record<number, string>
  ) {
    const gradient: CanvasGradient = this.context.createLinearGradient(
      x1,
      y1,
      x2,
      y2
    );
    // const gradientCustom: CustomCanvasGradient =
    //   this.customContext.createLinearGradient(x1, y1, x2, y2);
    if (colorStops) {
      Object.entries(colorStops).forEach(([key, value]) => {
        gradient.addColorStop(parseFloat(key), value);
        // gradientCustom.addColorStop(parseFloat(key), value);
      });
    }
    return gradient;
    // return {
    //   gradient
    //   // gradientCustom
    // };
  }

  createRadialGradient(
    x0: number,
    y0: number,
    r0: number,
    x1: number,
    y1: number,
    r1: number,
    colorStops?: Record<number, string>
  ) {
    const gradient: CanvasGradient = this.context.createRadialGradient(
      x0,
      y0,
      r0,
      x1,
      y1,
      r1
    );
    // const gradientCustom: CustomCanvasGradient =
    //   this.customContext.createRadialGradient(x0, y0, r0, x1, y1, r1);
    if (colorStops) {
      Object.entries(colorStops).forEach(([key, value]) => {
        gradient.addColorStop(parseFloat(key), value);
        // gradientCustom.addColorStop(parseFloat(key), value);
      });
    }
    return gradient;
    // return {
    //   gradient
    //   // gradientCustom
    // };
  }

  //  const image = new Image();
  //  image.src = "../assets/images/picture.jpg";
  //  image.onload = function () {
  //    co.drawImage(image);
  //  };
  drawImage(
    element:
      | HTMLOrSVGImageElement
      | HTMLVideoElement
      | HTMLCanvasElement
      | ImageBitmap
      | OffscreenCanvas,
    ...args: number[]
  ) {
    if (args.length === 0) {
      this.context.drawImage(element, 0, 0);
    } else if (args.length === 2) {
      const dx = args[0];
      const dy = args[1];
      this.context.drawImage(element, dx, dy);
    } else if (args.length === 4) {
      const dx = args[0];
      const dy = args[1];
      const dw = args[2];
      const dh = args[3];
      this.context.drawImage(element, dx, dy, dw, dh);
    } else if (args.length === 8) {
      const sx = args[0];
      const sy = args[1];
      const sw = args[2];
      const sh = args[3];
      const dx = args[4];
      const dy = args[5];
      const dw = args[6];
      const dh = args[7];
      this.context.drawImage(element, sx, sy, sw, sh, dx, dy, dw, dh);
    } else {
      throw new Error(
        "Invalid number of arguments passed to drawImage: " + args.length
      );
    }
    if (this.customContext) this.customContext.drawImage(element, ...args);
    return this;
  }

  getImageData(
    sx = 0,
    sy = 0,
    sw = this.canvas.width,
    sh = this.canvas.height,
    settings?: ImageDataSettings | undefined
  ): ImageData {
    return this.context.getImageData(sx, sy, sw, sh, settings);
    /// customContext Not yet implemented
  }

  putImageData(imageData: ImageData, dx = 0, dy = 0) {
    return this.context.putImageData(imageData, dx, dy);
    /// customContext Not yet implemented
  }

  createPattern(
    image:
      | HTMLImageElement
      | SVGImageElement
      | HTMLVideoElement
      | HTMLCanvasElement
      | ImageBitmap
      | OffscreenCanvas,
    repetition: "repeat" | "repeat-x" | "repeat-y" | "no-repeat" | null
  ): CanvasPattern | null {
    return this.context.createPattern(image, repetition);
    /// customContext Not yet implemented
  }

  toRawSVG() {
    if (this.customContext) {
      const serializedSVG: string = this.customContext.getSerializedSvg(true);
      const svgElement: SVGElement = this.customContext.getSvg();
      return { raw: serializedSVG, element: svgElement };
    } else {
      return { raw: "", element: null };
    }
  }
}

export default YZCanvasContext;
